import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, Transition as _Transition, withCtx as _withCtx, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Teleport as _Teleport, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "profile-setup-page pa-6" }
const _hoisted_2 = { class: "bottom-bar" }

import { computed, onMounted, ref } from "vue";
import type { Component } from "vue";
import { useRouter } from "vue-router";
import { fetchProfile, getActiveContext, saveActiveContext } from "@/modules/auth/services/auth.api";
import { getAuthToken, getCustomer, setCustomer } from "@/modules/auth/services/auth.storage";
import {
  updateBusinessAddress,
  updateGeneral,
  fetchVendorInfo,
  paymentCredential,
  schedule as setupProfileSchedule,
  schedulePackages,
  serviceList,
  servicePackages,
  taxSettings,
  updatePassword,
  uploadLogo,
} from "@/modules/vendor/services/setup-profile.api";
import StepProgressBar from "@/shared/components/ui/flow/StepProgressBar.vue";
import PasswordChangeStep from "@/modules/vendor/components/profile-setup/PasswordChangeStep.vue";
import GeneralSetupStep from "@/modules/vendor/components/profile-setup/GeneralSetupStep.vue";
import BusinessAddressSetupStep from "@/modules/vendor/components/profile-setup/BusinessAddressSetupStep.vue";
import UploadLogoStep from "@/modules/vendor/components/profile-setup/UploadLogoStep.vue";
import PaymentCredentialsStep from "@/modules/vendor/components/profile-setup/PaymentCredentialsStep.vue";
import ServiceTemplateStep from "@/modules/vendor/components/profile-setup/ServiceTemplateStep.vue";
import PackageTemplateStep from "@/modules/vendor/components/profile-setup/PackageTemplateStep.vue";
import SchedulePackageStep from "@/modules/vendor/components/profile-setup/SchedulePackageStep.vue";
import TaxSetupStep from "@/modules/vendor/components/profile-setup/TaxSetupStep.vue";
import ScheduleSetupStep from "@/modules/vendor/components/profile-setup/ScheduleSetupStep.vue";
import SetupCompleteStep from "@/modules/vendor/components/profile-setup/SetupCompleteStep.vue";
import AppCard from "@/shared/components/ui/AppCard.vue";

type StepExposed = { getValues?: () => unknown };
const currentStepLabelText = 'Account Setup';


export default /*@__PURE__*/_defineComponent({
  __name: 'ProfileSetupPage',
  setup(__props) {

const router = useRouter();

const activeContext = computed(() => getActiveContext() || {});
const profileSetup = computed(() => (activeContext.value as any)?.profile_setup || null);

const steps = [
  { current_step: 1, code: "password_change", label: "Password change", percent: 10 },
  { current_step: 2, code: "general", label: "General", percent: 20 },
  { current_step: 3, code: "business_address", label: "Business address", percent: 30 },
  { current_step: 4, code: "upload_logo", label: "Upload logo", percent: 40 },
  { current_step: 5, code: "payment_credentials", label: "Payment credentials", percent: 50 },
  { current_step: 6, code: "service_template", label: "Service template", percent: 60 },
  { current_step: 7, code: "package_template", label: "Create package template", percent: 70 },
  { current_step: 8, code: "schedule_package", label: "Schedule package", percent: 80 },
  { current_step: 9, code: "tax_setup", label: "Tax setup template", percent: 90 },
  { current_step: 10, code: "schedule_setup", label: "Schedule setup", percent: 95 },
  { current_step: 11, code: "setup_complete", label: "Setup complete", percent: 100 },
] as const;

const stepIndex = ref(0);
const stepTransitionName = ref("step-forward");
const stepRef = ref<StepExposed | null>(null);
const isStepSubmitting = ref(false);
const profileInfo = ref<any | null>(null);

// Add per-step errors store (each index holds a map of field -> message)
const stepErrors = ref<Array<Record<string, string> | null>>(Array(steps.length).fill(null));

function markProfileSetupComplete() {
  const context = (activeContext.value as any) || {};
  saveActiveContext({
    ...context,
    profile_setup: {
      ...(context.profile_setup || {}),
      setup_required: false,
      setup_completed: true,
    },
  });

  const customer: any = getCustomer<any>() || null;
  if (customer?.first_login === true) {
    setCustomer({ ...customer, first_login: false });
  }
}

async function refreshVendorInfo() {
  try {
    const response = await fetchVendorInfo();
    // eslint-disable-next-line no-console
    console.log("[profile-setup] vendors/info", { response });
    const data = response?.data ?? null;
    profileInfo.value = data;

    const apiCurrentStep = Number(data?.current_step);
    if (Number.isFinite(apiCurrentStep) && apiCurrentStep > 0) {
      stepIndex.value = Math.min(Math.max(apiCurrentStep, 1), steps.length) - 1;
    } else {
      stepIndex.value = serverStepIndex.value;
    }

    if (data?.is_completed === 1 || data?.is_completed === true) {
      router.replace(resolveDashboardRoute());
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("[profile-setup] vendors/info failed", error);
    profileInfo.value = null;
    stepIndex.value = serverStepIndex.value;
  }
}

const serverStepIndex = computed(() => {
  const customer: any = getCustomer<any>() || null;
  const isExplicitNotFirstLogin = customer?.first_login === false;
  const isFirstLogin = customer?.first_login === true;

  // Force the starting step based on first-login status.
  // We intentionally override any server-provided `current_step` here because the UX rule
  // is: first_login === true -> password step; first_login === false -> general step.
  if (isFirstLogin) return 0; // step 1
  if (isExplicitNotFirstLogin) return 1; // step 2

  // If first_login is missing, fall back to server current_step (or step 1).
  const step = Number(profileSetup.value?.current_step || 1);
  const normalized = Number.isFinite(step) && step > 0 ? step : 1;
  return Math.min(Math.max(normalized, 1), steps.length) - 1;
});

onMounted(async () => {
  await refreshVendorInfo();
});

const currentStepIndex = computed(() => steps[stepIndex.value]?.current_step ?? stepIndex.value + 1);

const currentStepPercent = computed(() => Math.round(((stepIndex.value + 1) / steps.length) * 100));

const currentStepComponent = computed<Component>(() => {
  if (stepIndex.value === 0) return PasswordChangeStep;
  if (stepIndex.value === 1) return GeneralSetupStep;
  if (stepIndex.value === 2) return BusinessAddressSetupStep;
  if (stepIndex.value === 3) return UploadLogoStep;
  if (stepIndex.value === 4) return PaymentCredentialsStep;
  if (stepIndex.value === 5) return ServiceTemplateStep;
  if (stepIndex.value === 6) return PackageTemplateStep;
  if (stepIndex.value === 7) return SchedulePackageStep;
  if (stepIndex.value === 8) return TaxSetupStep;
  if (stepIndex.value === 9) return ScheduleSetupStep;
  return SetupCompleteStep;
});

const currentStepKey = computed(() => `profile-setup-step-${stepIndex.value}`);

function resolveDashboardRoute() {
  // Vendor-specific profile setup always routes to vendor dashboard
  return { name: "vendor.dashboard" };
}

function handlePrevious() {
  stepTransitionName.value = "step-back";
  if (stepIndex.value > 0) stepIndex.value -= 1;
}

function logCurrentStepValues() {
  const step = steps[stepIndex.value];
  const code = step?.code ?? "unknown_step";
  const label = step?.label ?? "Unknown step";
  const fields = stepRef.value?.getValues?.() ?? null;
  // eslint-disable-next-line no-console
  console.log("[profile-setup]", {
    stepIndex: stepIndex.value,
    stepNumber: stepIndex.value + 1,
    code,
    label,
    fields,
  });
}

async function runStepAction(fields: unknown) {
  const step = steps[stepIndex.value];
  const code = step?.code ?? "unknown_step";

  if (code === "password_change") {
    const f = (fields ?? {}) as any;
    const response = await updatePassword({
      password: f?.newPassword,
      password_confirmation: f?.confirmPassword,
      current_step: currentStepIndex.value, // Inform server of the current step we are on
    });
    // eslint-disable-next-line no-console
    console.log("[profile-setup] password_change response", response);
    try {
      await fetchProfile();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[profile-setup] fetchProfile failed after password change", err);
    }
    return;
  }

  if (code === "general") {
    const f = (fields ?? {}) as any;
    const businessType = String(f?.business_type || "").trim();
    const serviceCategory = String(f?.service_category || "").trim();
    const businessEmail = String(f?.business_email || "").trim();
    const businessPhone = String(f?.business_phone || "").trim();
    const taxId = String(f?.tax_id || "").trim();

    if (!businessType || !serviceCategory) {
      throw {
        errors: {
          business_type: businessType ? "" : "Business type is required.",
          service_category: serviceCategory ? "" : "Service type is required.",
        },
      };
    }

    const response = await updateGeneral({
      business_email: businessEmail,
      business_phone: businessPhone,
      business_type: businessType,
      service_category: serviceCategory,
      tax_id: taxId,
      current_step: currentStepIndex.value,
    });
    // eslint-disable-next-line no-console
    console.log("[profile-setup] general response", response);
    return;
  }

  if (code === "business_address") {
    const f = (fields ?? {}) as any;
    const line1 = String(f?.line_1 || "").trim();
    const line2 = String(f?.line_2 || "").trim();
    const city = String(f?.city || "").trim();
    const state = String(f?.state || "").trim();
    const zip = String(f?.zip || "").trim();
    const country = String(f?.country || "").trim();
    const latitude = String(f?.latitude || "").trim();
    const longitude = String(f?.longitude || "").trim();

    if (!line1 || !city || !state || !zip) {
      throw {
        errors: {
          line_1: line1 ? "" : "Address line 1 is required.",
          city: city ? "" : "City is required.",
          state: state ? "" : "State is required.",
          zip: zip ? "" : "ZIP is required.",
        },
      };
    }

    const response = await updateBusinessAddress({
      line_1: line1,
      line_2: line2,
      city,
      state,
      zip,
      country,
      latitude,
      longitude,
      current_step: currentStepIndex.value,
    });
    // eslint-disable-next-line no-console
    console.log("[profile-setup] business_address response", response);
    return;
  }

  if (code === "upload_logo") {
    const f = (fields ?? {}) as any;
    const payload = new FormData();
    const hasPrefilledLogo = Boolean(profileInfo.value?.logo?.url);
    const skip = hasPrefilledLogo && !f?.logoFile;

    if (f?.logoFile) payload.append("logo", f.logoFile);
    payload.append("current_step", String(currentStepIndex.value));
    payload.append("skip", skip ? "true" : "false");
    const response = await uploadLogo(payload);
    // eslint-disable-next-line no-console
    console.log("[profile-setup] upload_logo response", response);
    return;
  }

  if (code === "payment_credentials") {
    const f = (fields ?? {}) as any;
    const existingPayment = profileInfo.value?.payment_credentials || null;
    const existingCreds = existingPayment?.credentials || null;
    const hasPrefilledCreds = Boolean(existingCreds);
    const prefilledName = typeof existingPayment?.name === "string" ? existingPayment.name : "";
    const existingIdempotencyKey = String(
      existingCreds?.idempotency_key ?? existingPayment?.idempotency_key ?? "",
    ).trim();

    const nextName = String(f?.name ?? "");
    const nextPublishable = String(f?.publicKey ?? "");
    const nextSecret = String(f?.secretKey ?? "");

    const skip =
      hasPrefilledCreds &&
      nextName === prefilledName &&
      nextPublishable === String(existingCreds?.publishable_key ?? "") &&
      nextSecret === String(existingCreds?.secret_key ?? "");

    const response = await paymentCredential({
      current_step: currentStepIndex.value,
      skip,
      name: f?.name,
      mode:'test',
      credentials:{
        publishable_key: f?.publicKey,
        secret_key: f?.secretKey,
        ...(existingIdempotencyKey ? { idempotency_key: existingIdempotencyKey } : {}),
      }
    });
    // eslint-disable-next-line no-console
    console.log("[profile-setup] payment_credentials response", response);

    // Some APIs return HTTP 200 with { success: false }. Treat that as an error and stay on the same step.
    const isSuccessFalse =
      response &&
      typeof response === "object" &&
      (((response as any).success === false) || ((response as any).status === false));
    if (isSuccessFalse) {
      const errs =
        (response as any)?.errors && typeof (response as any).errors === "object"
          ? (response as any).errors
          : (() => {
              const msg = String((response as any)?.message || "").trim();
              const err = String((response as any)?.error || "").trim();
              const combined = [msg, err].filter(Boolean).join(" ");
              return { message: combined || "Payment credential update failed." };
            })();
      throw { errors: errs };
    }

    return;
  }

  if (code === "service_template") {
    const f = (fields ?? {}) as any;
    const services = (f?.services || []).map((service: any) => ({
      service_name: service.service_name,
      subtitle: service.subtitle,
      price: service.price || 0
    }));
    const response = await serviceList({
      services,
      current_step: currentStepIndex.value,
    });
    // eslint-disable-next-line no-console
    console.log("[profile-setup] service_template response", response);
    return;
  }

  if (code === "package_template") {
    const f = (fields ?? {}) as any;
    const existingId = profileInfo.value?.service_packages?.id ?? null;
    const id = f?.id ?? existingId ?? null;
    const response = await servicePackages({ 
      current_step: currentStepIndex.value,
      package_list: f?.packages || [],
      ...(id ? { id } : {}),
    });
    // eslint-disable-next-line no-console
    console.log("[profile-setup] package_template response", response);
    return;
  }

  if (code === "schedule_package") {
    const f = (fields ?? {}) as any;
    const existingId = profileInfo.value?.schedule_packages?.id ?? null;
    const id = f?.id ?? existingId ?? null;
    const response = await schedulePackages({
      current_step: currentStepIndex.value,
      schedule_packages: f?.schedule_packages || [],
      ...(id ? { id } : {}),
    });
    // eslint-disable-next-line no-console
    console.log("[profile-setup] schedule_package response", response);
    return;
  }

  if (code === "tax_setup") {
    const f = (fields ?? {}) as any;
    const response = await taxSettings({ 
      ...f,
      current_step: currentStepIndex.value,
    });
    // eslint-disable-next-line no-console
    console.log("[profile-setup] tax_setup response", response);
    return;
  }

  if (code === "schedule_setup") {
    const f = (fields ?? {}) as any;
    const activeDays = Object.entries(f.schedule || {})
      .filter(([_, day]: [string, any]) => day.enabled)
      .map(([dayKey, _]) => dayKey);
    
    const response = await setupProfileSchedule({ 
      working_days: activeDays,
      start_time: f.schedule?.mon?.open || "09:00",
      end_time: f.schedule?.mon?.close || "17:00",
      current_step: currentStepIndex.value,
    });
    // eslint-disable-next-line no-console
    console.log("[profile-setup] schedule_setup response", response);
    return;
  }
  if (code === "setup_complete") {
    markProfileSetupComplete();
    router.push({ name: "vendor.dashboard" });
    return;
  }
  // eslint-disable-next-line no-console
  console.log("[profile-setup] no API wired for step", { code });
}

async function handleNext() {
  if (isStepSubmitting.value) return;
  stepTransitionName.value = "step-forward";

  const fields = stepRef.value?.getValues?.() ?? null;
  logCurrentStepValues();

  try {
    isStepSubmitting.value = true;
    // Clear previous errors for this step before submitting
    stepErrors.value[stepIndex.value] = null;

    await runStepAction(fields);
    // Always refresh vendor info on each Next click (keeps prefilled step data in sync)
    await refreshVendorInfo();
    if (stepIndex.value < steps.length - 1) stepIndex.value += 1;
  } catch (error: any) {
    // If the API returned validation errors, map them into stepErrors for the current step
    const payload = error?.response?.data?.errors ?? error?.errors ?? null;
    if (payload && typeof payload === "object") {
      const mapped: Record<string, string> = {};
      Object.entries(payload).forEach(([k, v]) => {
        if (Array.isArray(v)) mapped[k] = v.join(" ");
        else mapped[k] = String(v ?? "");
      });
      stepErrors.value[stepIndex.value] = mapped;
    } else {
      // eslint-disable-next-line no-console
      console.error("[profile-setup] step action failed", error);
    }
  } finally {
    isStepSubmitting.value = false;
  }
}

async function handleSkipToDashboard() {
  if (isStepSubmitting.value) return;

  const fields = stepRef.value?.getValues?.() ?? null;
  logCurrentStepValues();

  try {
    isStepSubmitting.value = true;
    // Clear previous errors for this step before submitting
    stepErrors.value[stepIndex.value] = null;

    await runStepAction(fields);
    router.push(resolveDashboardRoute());
  } catch (error: any) {
    const payload = error?.response?.data?.errors ?? error?.errors ?? null;
    if (payload && typeof payload === "object") {
      const mapped: Record<string, string> = {};
      Object.entries(payload).forEach(([k, v]) => {
        if (Array.isArray(v)) mapped[k] = v.join(" ");
        else mapped[k] = String(v ?? "");
      });
      stepErrors.value[stepIndex.value] = mapped;
    } else {
      // eslint-disable-next-line no-console
      console.error("[profile-setup] step action failed", error);
    }
  } finally {
    isStepSubmitting.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(StepProgressBar, {
      label: currentStepLabelText,
      percent: currentStepPercent.value,
      "current-step": currentStepIndex.value,
      "total-steps": steps.length
    }, null, 8, ["percent", "current-step", "total-steps"]),
    _createVNode(AppCard, { class: "mt-8" }, {
      default: _withCtx(() => [
        _createVNode(_Transition, {
          name: stepTransitionName.value,
          mode: "out-in"
        }, {
          default: _withCtx(() => [
            (_openBlock(), _createBlock(_resolveDynamicComponent(currentStepComponent.value), {
              key: currentStepKey.value,
              ref_key: "stepRef",
              ref: stepRef,
              current_step: currentStepIndex.value,
              errors: stepErrors.value[stepIndex.value],
              "profile-info": profileInfo.value
            }, null, 8, ["current_step", "errors", "profile-info"]))
          ]),
          _: 1
        }, 8, ["name"])
      ]),
      _: 1
    }),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_component_v_btn, {
          class: "bottom-btn action-btn",
          variant: "flat",
          color: "gray-darken-1",
          size: "large",
          "prepend-icon": "mdi-arrow-left",
          disabled: stepIndex.value === 0 || isStepSubmitting.value,
          onClick: handlePrevious
        }, {
          default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
            _createTextVNode(" Previous ", -1)
          ]))]),
          _: 1
        }, 8, ["disabled"]),
        (stepIndex.value < steps.length - 1)
          ? (_openBlock(), _createBlock(_component_v_btn, {
              key: 0,
              class: "bottom-btn action-btn next-btn",
              color: "primary",
              size: "large",
              variant: "flat",
              "append-icon": "mdi-arrow-right",
              loading: isStepSubmitting.value,
              disabled: isStepSubmitting.value,
              onClick: handleNext
            }, {
              default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                _createTextVNode(" Next ", -1)
              ]))]),
              _: 1
            }, 8, ["loading", "disabled"]))
          : (_openBlock(), _createBlock(_component_v_btn, {
              key: 1,
              class: "bottom-btn action-btn next-btn",
              color: "primary",
              size: "large",
              variant: "flat",
              "append-icon": "mdi-check",
              loading: isStepSubmitting.value,
              disabled: isStepSubmitting.value,
              onClick: handleSkipToDashboard
            }, {
              default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                _createTextVNode(" Finish ", -1)
              ]))]),
              _: 1
            }, 8, ["loading", "disabled"]))
      ])
    ]))
  ]))
}
}

})